import { jsonToQueryString } from '@shopee/url-util';
import { isFetchedInRange } from '@shopee/list-redux-util';
import {
  SEARCH_PARAMS_TO_API_PARAMETER_NAMES,
  PRIMITIVE_TYPE_SEARCH_PARAMS,
  BOOLEAN_TYPE_SEARCH_PARAMS,
  ARRAY_TYPE_SEARCH_PARAMS,
} from './constants';
import {
  SORT_METHOD,
  SORT_ORDER,
  MART_SEARCH_RESULT_TYPE,
} from '@shopee/search-common-util';
import PROGRESS from '@shopee/api-progress';

// Types
import type { SearchParams } from '@shopee/search-common-util';
import type {
  MartSearchItemsResponse,
  MartSearchResult,
  MartSearchItemsStore,
} from './types';
import type { ProgressType } from '@shopee/api-progress';

/* eslint-disable max-statements */
export function formatMartSearchItemsParameters(
  searchParams: SearchParams,
  options?: {
    offset?: number;
    limit?: number;
    keyOnly?: boolean;
    customField?: boolean;
  }
) {
  const { offset, limit, keyOnly, customField } = options || {};
  const params: Record<string, any> = {
    ...formatSearchSortParameters(searchParams),
    ...formatPriceFilterParameters(searchParams),
  };

  PRIMITIVE_TYPE_SEARCH_PARAMS.forEach(key => {
    if (searchParams[key]) {
      params[SEARCH_PARAMS_TO_API_PARAMETER_NAMES[key]] = searchParams[key];
    }
  });

  BOOLEAN_TYPE_SEARCH_PARAMS.forEach(key => {
    if (searchParams[key]) {
      params[SEARCH_PARAMS_TO_API_PARAMETER_NAMES[key]] = '1';
    }
  });

  ARRAY_TYPE_SEARCH_PARAMS.forEach(key => {
    const value = searchParams[key];
    if (value && value.length > 0) {
      params[SEARCH_PARAMS_TO_API_PARAMETER_NAMES[key]] = value;
    }
  });

  if (searchParams.thirdCategory) {
    params.match_id = searchParams.thirdCategory;
    params.match_case = 'category';
  } else if (searchParams.subcategory) {
    params.match_id = searchParams.subcategory;
    params.match_case = 'category';
  } else if (searchParams.category) {
    params.match_id = searchParams.category;
    params.match_case = 'category';
  }

  if (searchParams.collection) {
    params.match_case = 'collection';
    params.match_id = searchParams.collection;
  }

  if (searchParams.shop && searchParams.shopCollection) {
    params.match_case = 'shop_category';
    params.match_id = searchParams.shop;
    params.shop_categoryids = searchParams.shopCollection;
  }

  if (searchParams.promotionId) {
    params.promotionid = searchParams.promotionId;
  }

  if (!keyOnly) {
    params.newest = offset;
    params.limit = limit;
  }

  if (customField) {
    params.fromMpi = searchParams.fromMpi;
  }

  return jsonToQueryString(params);
}
/* eslint-enable max-statements */

function formatSearchSortParameters(
  searchParams: SearchParams
): Record<string, any> {
  const params: Record<string, any> = {};

  params.by = searchParams.sortBy || SORT_METHOD.RELEVANCY;
  if (params.by === SORT_METHOD.PRICE) {
    params.order = searchParams.order;
  } else {
    params.order = SORT_ORDER.DESC;
  }

  return params;
}

function formatPriceFilterParameters(
  searchParams: SearchParams
): Record<string, any> {
  const params: Record<string, any> = {};

  const { minPrice: minPriceParams, maxPrice: maxPriceParams } = searchParams;
  const minPrice =
    typeof minPriceParams === 'number'
      ? minPriceParams
      : minPriceParams
      ? parseInt(minPriceParams, 10)
      : null;
  const maxPrice =
    typeof maxPriceParams === 'number'
      ? maxPriceParams
      : maxPriceParams
      ? parseInt(maxPriceParams, 10)
      : null;
  if (minPrice !== null && !isNaN(minPrice)) {
    params.price_min = minPrice;
  }
  if (maxPrice !== null && !isNaN(maxPrice)) {
    params.price_max = maxPrice;
  }

  return params;
}

export function getStateKey(searchParams: SearchParams) {
  return formatMartSearchItemsParameters(searchParams, {
    keyOnly: true,
    customField: true,
  });
}

const FALLBACK_SEARCH_RESULT: MartSearchResult = {
  total_count: 0,
  nomore: false,
  adjust: {
    count: 0,
  },
  show_disclaimer: false,
  disclaimer_infos: [],
  items: [],
  query_rewrite: {
    fe_query_write_status: MART_SEARCH_RESULT_TYPE.NORMAL,
    rewrite_keyword: null,
    hint_keywords: null,
    ori_keyword: null,
    ori_totalCount: 0,
    rewrite_type: null,
  },
  nextOffset: 0,
  progress: PROGRESS.INIT,
  fetchedRange: [[0, 0]],
};

// eslint-disable-next-line complexity
export function processRawResponse(
  response: MartSearchItemsResponse,
  offset: number,
  limit: number
): MartSearchResult {
  if (response.error || !response.data) {
    return FALLBACK_SEARCH_RESULT;
  }
  const {
    data: {
      algorithm,
      total_count,
      nomore,
      adjust,
      show_disclaimer,
      disclaimer_infos,
      json_data,
      query_rewrite,
      items,
    },
  } = response;

  const queryRewriteStatus =
    query_rewrite && query_rewrite.fe_query_write_status;

  return {
    algorithm: algorithm || void 0,
    total_count: total_count || 0,
    nomore: nomore || false,
    adjust: {
      count: (adjust && adjust.count) || 0,
    },
    show_disclaimer: show_disclaimer || false,
    disclaimer_infos: disclaimer_infos || [],
    json_data: json_data || void 0,
    items: (items || []).map(item => {
      const { item_basic, ...otherFields } = item;
      return {
        ...item_basic,
        ...otherFields,
      };
    }),
    query_rewrite: {
      fe_query_write_status:
        typeof queryRewriteStatus === 'number'
          ? queryRewriteStatus
          : (total_count || 0) > 0
          ? MART_SEARCH_RESULT_TYPE.NORMAL
          : MART_SEARCH_RESULT_TYPE.NO_RESULT,
      rewrite_keyword: query_rewrite && query_rewrite.rewrite_keyword,
      hint_keywords: query_rewrite && query_rewrite.hint_keywords,
      ori_keyword: query_rewrite && query_rewrite.ori_keyword,
      ori_totalCount: query_rewrite && query_rewrite.ori_totalCount,
      rewrite_type: query_rewrite && query_rewrite.rewrite_type,
    },
    nextOffset: 0,
    progress: PROGRESS.OK,
    fetchedRange: [[offset, offset + limit]],
  };
}

const SHOULD_FETCH_PROGRESS: ProgressType[] = [PROGRESS.INIT, PROGRESS.ERR];

export function getDefaultShouldSkipCall({
  getStore,
  stateKey,
  offset,
  limit,
}: {
  getStore: (state: Record<string, unknown>) => MartSearchItemsStore | void;
  stateKey: string;
  offset: number;
  limit: number;
}) {
  return (state: Record<string, unknown>): boolean => {
    const store = getStore(state);
    if (!store) {
      return true;
    }
    const searchResult = store[stateKey];
    if (
      !searchResult ||
      SHOULD_FETCH_PROGRESS.includes(searchResult.progress)
    ) {
      return false;
    } else if (searchResult.progress === PROGRESS.REQ) {
      return true;
    }
    return isFetchedInRange(searchResult.fetchedRange, offset, limit);
  };
}
